import { useEffect, useMemo, useState } from "react";
import { Plus, Warehouse, Tag, Upload, History, Clock, PackagePlus, RotateCcw, XCircle, ArrowRight, ShoppingCart, Home, MapPinned, Bookmark, BookmarkX } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { formatDMY, formatDMYTime } from "@/lib/date";
import { useAuth } from "@/context/AuthContext";
import { RentalDialog } from "@/components/RentalDialog";
import { ItemCodeTag } from "@/components/ItemCode";
import { can } from "@/lib/perms";

const STATUSES = ["In Stock", "Demo Pool", "Reserved for Demo", "Reserved", "Used", "Sold-Dispatched", "Rental"];
const STATUS_STYLES = {
  "In Stock": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Demo Pool": "bg-sky-50 text-sky-700 border-sky-200",
  "Reserved for Demo": "bg-amber-50 text-amber-700 border-amber-200",
  "Reserved": "bg-orange-50 text-orange-700 border-orange-200",
  "Used": "bg-purple-50 text-purple-700 border-purple-200",
  "Sold-Dispatched": "bg-slate-100 text-slate-600 border-slate-200",
  "Rental": "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-300",
};

// Manual transition rules — hide invalid options client-side.
// Business rule: In-Stock is sale-only. Demos are booked ONLY from Demo Pool.
// Reservations use their own reserve/release endpoints — not the plain status dropdown.
// Admin can reclaim a Used unit back to In Stock / Demo Pool if no active demo booking holds it (server enforces this).
function allowedTransitions(current, isAdmin = false, canRental = false) {
  if (current === "Sold-Dispatched") return ["Sold-Dispatched"]; // terminal
  if (current === "Reserved for Demo") return ["Reserved for Demo"]; // managed by booking flow only
  if (current === "Reserved") return ["Reserved"]; // managed by reserve/release flow
  if (current === "Rental") return canRental ? ["Rental", "In Stock", "Demo Pool", "Sold-Dispatched"] : ["Rental"];
  const rental = canRental ? ["Rental"] : [];
  if (current === "Used") {
    return isAdmin
      ? ["Used", "In Stock", "Demo Pool", "Sold-Dispatched", ...rental]
      : ["Used", "Sold-Dispatched"];
  }
  if (current === "Demo Pool") return ["Demo Pool", "In Stock", "Sold-Dispatched", ...rental]; // admin can pull back or sell direct
  return ["In Stock", "Demo Pool", "Sold-Dispatched", ...rental]; // from In Stock — can move to demo pool or sell
}

const RentalCell = ({ s, onEdit, testId }) => (
  <div data-testid={testId}>
    {s.rental_client ? <div className="text-slate-900 font-medium truncate">{s.rental_client.company_name}</div> : <div className="text-fuchsia-700 text-xs font-medium">On rental</div>}
    <div className="text-xs text-slate-500 truncate">{s.rental_site || s.location || "—"}</div>
    {onEdit && <button type="button" onClick={onEdit} className="text-[11px] text-fuchsia-700 hover:underline" data-testid={`${testId}-edit`}>Edit rental details</button>}
  </div>
);

export default function Inventory() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin" || me?.role_name === "Admin";
  const canRental = isAdmin || me?.role_name === "Manager";
  const [rentalUnit, setRentalUnit] = useState(null);
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ catalog_id: "", location: "", status: "" });
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ catalog_id: "", serial_number: "", location: "", status: "In Stock", received_date: new Date().toISOString().slice(0, 10), nickname: "" });
  const [saving, setSaving] = useState(false);
  const [sellUnit, setSellUnit] = useState(null);
  const [nickUnit, setNickUnit] = useState(null);
  const [historyUnit, setHistoryUnit] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [reserveUnit, setReserveUnit] = useState(null);
  const [releaseUnit, setReleaseUnit] = useState(null);

  const needle = search.trim().toLowerCase();
  const shown = needle
    ? items.filter((s) => [s.serial_number, s.nickname, s.catalog?.model_name, s.catalog?.item_code].some((v) => (v || "").toLowerCase().includes(needle)))
    : items;

  const load = async () => {
    const qs = new URLSearchParams();
    if (filters.catalog_id) qs.append("catalog_id", filters.catalog_id);
    if (filters.location) qs.append("location", filters.location);
    if (filters.status) qs.append("status", filters.status);
    try {
      const res = await api.get(`/inventory?${qs.toString()}`);
      setItems(res.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [c, cli] = await Promise.all([api.get("/catalog"), api.get("/clients")]);
        setCatalog(c.data); setClients(cli.data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [filters]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/inventory", form);
      toast.success("Stock unit added");
      setOpen(false);
      setForm({ catalog_id: "", serial_number: "", location: "", status: "In Stock", received_date: new Date().toISOString().slice(0, 10), nickname: "" });
      load();
    } catch (e2) { toast.error(formatApiError(e2)); } finally { setSaving(false); }
  };

  const updateStatus = async (id, status, item) => {
    if (status === "Sold-Dispatched" && item.status !== "Sold-Dispatched") {
      setSellUnit(item);
      return;
    }
    if (status === "Rental" && item.status !== "Rental") {
      setRentalUnit(item);
      return;
    }
    try {
      await api.put(`/inventory/${id}`, { ...item, status });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="inventory-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Warehouse</div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">Stock inventory</h1>
          <p className="text-slate-500 text-sm mt-1 hidden md:block">Every physical unit — serialized and tracked from receiving through sale.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {can(me, "inventory", "edit") && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="bulk-import-btn" className="flex-1 md:flex-none">
            <Upload className="w-4 h-4 mr-1.5" /> Bulk import
          </Button>}
          <Dialog open={open} onOpenChange={setOpen}>
            {can(me, "inventory", "edit") && <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700 flex-1 md:flex-none" data-testid="add-stock-btn"><Plus className="w-4 h-4 mr-1.5" /> Add stock</Button>
            </DialogTrigger>}
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Log new stock unit</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Machine model *</Label>
                  <Select value={form.catalog_id} onValueChange={(v) => setForm({ ...form, catalog_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="stock-model-select"><SelectValue placeholder="Select model" /></SelectTrigger>
                    <SelectContent>
                      {catalog.map((c) => <SelectItem key={c.id} value={c.id}>{c.model_name} — {c.manufacturer}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Serial number *</Label>
                  <Input required value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} data-testid="stock-serial-input" className="mt-1 font-mono" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nickname / name (optional)</Label>
                  <Input value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} data-testid="stock-nickname-input" className="mt-1" placeholder="e.g. 'Blue press'" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Warehouse / branch *</Label>
                  <Input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="stock-location-input" className="mt-1" placeholder="e.g. Mumbai HQ" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status *</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger className="mt-1" data-testid="stock-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["In Stock", "Demo Pool"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Received date *</Label>
                    <Input required type="date" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} data-testid="stock-date-input" className="mt-1" />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={saving || !form.catalog_id} className="bg-indigo-600 hover:bg-indigo-700" data-testid="stock-save-btn">
                    {saving ? "Saving..." : "Add unit"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <Input placeholder="Search serial, name, model or item code" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="inventory-search-input" />
        <Select value={filters.catalog_id || "all"} onValueChange={(v) => setFilters({ ...filters, catalog_id: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-model-select"><SelectValue placeholder="All models" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All models</SelectItem>
            {catalog.map((c) => <SelectItem key={c.id} value={c.id}>{c.model_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Filter by location" value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} data-testid="filter-location-input" />
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-status-select"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Serial / Nickname</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Model</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Location / Sold to</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">History</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-10" data-testid="inventory-empty">
                  <Warehouse className="w-8 h-8 mx-auto mb-2 text-slate-300" /> No stock records match.
                </TableCell></TableRow>
              ) : (
                shown.map((s) => {
                  const opts = allowedTransitions(s.status, isAdmin, canRental);
                  return (
                    <TableRow key={s.id} className="hover:bg-slate-50" data-testid={`inventory-row-${s.id}`}>
                      <TableCell>
                        <div className="font-mono text-sm text-slate-900">{s.serial_number}</div>
                        <button onClick={() => setNickUnit(s)} data-testid={`nickname-btn-${s.id}`}
                          className="text-xs text-slate-500 hover:text-indigo-600 inline-flex items-center gap-1 mt-0.5">
                          <Tag className="w-3 h-3" />
                          {s.nickname ? s.nickname : <span className="italic">add name</span>}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="text-slate-900 font-medium text-sm">{s.catalog?.model_name || "—"}</div>
                        {s.catalog && <div className="mt-0.5"><ItemCodeTag code={s.catalog.item_code} testId={`inv-item-code-${s.id}`} /></div>}
                        <div className="text-xs text-slate-500">{s.catalog?.manufacturer}</div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {s.status === "Sold-Dispatched" && s.sold_to_client ? (
                          <div>
                            <div className="text-slate-900 font-medium">{s.sold_to_client.company_name}</div>
                            <div className="text-xs text-slate-500">{s.sold_to_address_label || "—"}</div>
                            {typeof s.days_until_next_amc === "number" && (
                              <div className={`text-xs mt-0.5 ${s.amc_overdue ? "text-red-700 font-semibold" : "text-slate-500"}`} data-testid={`amc-countdown-${s.id}`}>
                                {s.amc_overdue ? `AMC ${Math.abs(s.days_until_next_amc)}d overdue` : `Next AMC in ${s.days_until_next_amc}d`}
                              </div>
                            )}
                          </div>
                        ) : s.status === "Rental" ? (
                          <RentalCell s={s} onEdit={canRental ? () => setRentalUnit(s) : null} testId={`rental-cell-${s.id}`} />
                        ) : s.status === "Reserved" ? (
                          <div data-testid={`reservation-cell-${s.id}`}>
                            <div className="text-orange-800 font-medium inline-flex items-center gap-1">
                              <Bookmark className="w-3.5 h-3.5" />
                              {s.reserved_for_quotation
                                ? `Quotation ${s.reserved_for_quotation.quotation_number}`
                                : (s.reserved_for_lead ? `Lead: ${s.reserved_for_lead.company_name}` : "Reserved")}
                            </div>
                            {s.reserved_for_quotation && s.reserved_for_lead && (
                              <div className="text-xs text-slate-500">for {s.reserved_for_lead.company_name}</div>
                            )}
                            <div className="text-xs text-slate-500">by {s.reserved_by_name || "—"}</div>
                            <div className="text-slate-600 text-xs">{s.location}</div>
                          </div>
                        ) : (
                          <div className="text-slate-600">{s.location}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select value={s.status} onValueChange={(v) => updateStatus(s.id, v, s)} disabled={s.status === "Sold-Dispatched" || s.status === "Reserved for Demo" || s.status === "Reserved" || (s.status === "Rental" && !canRental)}>
                          <SelectTrigger className={`h-8 w-[180px] text-xs border ${STATUS_STYLES[s.status] || ""}`} data-testid={`status-select-${s.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {opts.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          {(s.status === "In Stock" || s.status === "Demo Pool") && (
                            <Button size="sm" variant="outline" onClick={() => setReserveUnit(s)} data-testid={`reserve-btn-${s.id}`} className="text-orange-700 border-orange-200 hover:bg-orange-50">
                              <Bookmark className="w-3.5 h-3.5 mr-1" /> Reserve
                            </Button>
                          )}
                          {s.status === "Reserved" && (
                            <Button size="sm" variant="outline" onClick={() => setReleaseUnit(s)} data-testid={`release-btn-${s.id}`} className="text-slate-700 border-slate-200">
                              <BookmarkX className="w-3.5 h-3.5 mr-1" /> Release
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => setHistoryUnit(s)} data-testid={`history-btn-${s.id}`}>
                            <History className="w-3.5 h-3.5 mr-1" /> History
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        {/* Mobile cards */}
        <ul className="md:hidden divide-y divide-slate-100">
          {shown.length === 0 ? (
            <li className="text-center text-slate-400 py-10" data-testid="inventory-empty-m">
              <Warehouse className="w-8 h-8 mx-auto mb-2 text-slate-300" /> No stock records match.
            </li>
          ) : shown.map((s) => {
            const opts = allowedTransitions(s.status, isAdmin, canRental);
            return (
              <li key={s.id} className="p-4" data-testid={`inventory-card-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm text-slate-900 truncate">{s.serial_number}</div>
                    <button onClick={() => setNickUnit(s)} data-testid={`nickname-btn-m-${s.id}`}
                      className="text-xs text-slate-500 hover:text-indigo-600 inline-flex items-center gap-1 mt-0.5">
                      <Tag className="w-3 h-3" />
                      {s.nickname ? s.nickname : <span className="italic">add name</span>}
                    </button>
                    <div className="mt-1.5">
                      <div className="text-sm font-medium text-slate-900 truncate">{s.catalog?.model_name || "—"}</div>
                      {s.catalog?.item_code && <div className="font-mono text-[11px] text-slate-500" data-testid={`inv-item-code-m-${s.id}`}>{s.catalog.item_code}</div>}
                      <div className="text-xs text-slate-500 truncate">{s.catalog?.manufacturer}</div>
                    </div>
                  </div>
                  <div className="shrink-0">
                    <Select value={s.status} onValueChange={(v) => updateStatus(s.id, v, s)} disabled={s.status === "Sold-Dispatched" || s.status === "Reserved for Demo" || s.status === "Reserved" || (s.status === "Rental" && !canRental)}>
                      <SelectTrigger className={`h-9 w-auto min-w-[140px] text-[11px] border ${STATUS_STYLES[s.status] || ""}`} data-testid={`status-select-m-${s.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {opts.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-2 text-sm">
                  {s.status === "Sold-Dispatched" && s.sold_to_client ? (
                    <div>
                      <div className="text-slate-900 font-medium truncate">{s.sold_to_client.company_name}</div>
                      <div className="text-xs text-slate-500 truncate">{s.sold_to_address_label || "—"}</div>
                      {typeof s.days_until_next_amc === "number" && (
                        <div className={`text-xs mt-0.5 ${s.amc_overdue ? "text-red-700 font-semibold" : "text-slate-500"}`} data-testid={`amc-countdown-m-${s.id}`}>
                          {s.amc_overdue ? `AMC ${Math.abs(s.days_until_next_amc)}d overdue` : `Next AMC in ${s.days_until_next_amc}d`}
                        </div>
                      )}
                    </div>
                  ) : s.status === "Rental" ? (
                    <RentalCell s={s} onEdit={canRental ? () => setRentalUnit(s) : null} testId={`rental-cell-m-${s.id}`} />
                  ) : (
                    <div className="text-xs text-slate-500 truncate">{s.location}</div>
                  )}
                </div>
                <div className="mt-3">
                  <Button size="sm" variant="outline" onClick={() => setHistoryUnit(s)} data-testid={`history-btn-m-${s.id}`}>
                    <History className="w-3.5 h-3.5 mr-1" /> History
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <RentalDialog unit={rentalUnit} clients={clients} onClose={() => setRentalUnit(null)} onSaved={() => { setRentalUnit(null); load(); }} />
      <SellDialog unit={sellUnit} clients={clients} onClose={() => setSellUnit(null)} onSold={() => { setSellUnit(null); load(); }} />
      <NicknameDialog unit={nickUnit} onClose={() => setNickUnit(null)} onSaved={() => { setNickUnit(null); load(); }} />
      <MachineHistoryDialog unit={historyUnit} onClose={() => setHistoryUnit(null)} />
      <BulkImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={() => { setImportOpen(false); load(); }} />
      <ReserveDialog unit={reserveUnit} onClose={() => setReserveUnit(null)} onReserved={() => { setReserveUnit(null); load(); }} />
      <ReleaseDialog unit={releaseUnit} onClose={() => setReleaseUnit(null)} onReleased={() => { setReleaseUnit(null); load(); }} />
    </div>
  );
}

// ---------- Sell ----------
function SellDialog({ unit, clients, onClose, onSold }) {
  const [clientId, setClientId] = useState("");
  const [addressId, setAddressId] = useState("");
  const [nickname, setNickname] = useState("");
  const [soldDate, setSoldDate] = useState(new Date().toISOString().slice(0, 10));
  const [clientDetail, setClientDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setClientId(""); setAddressId(""); setNickname(unit?.nickname || ""); setSoldDate(new Date().toISOString().slice(0, 10)); setClientDetail(null);
  }, [unit]);
  useEffect(() => {
    if (!clientId) { setClientDetail(null); setAddressId(""); return; }
    (async () => {
      try { const r = await api.get(`/clients/${clientId}`); setClientDetail(r.data); setAddressId("__billing__"); } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [clientId]);
  if (!unit) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (!clientId) { toast.error("Please select the client this unit is sold to"); return; }
    if (!addressId) { toast.error("Please select the site address"); return; }
    setSaving(true);
    try {
      await api.patch(`/inventory/${unit.id}/sell`, { client_id: clientId, address_id: addressId, nickname, sold_date: soldDate });
      toast.success("Marked as Sold-Dispatched");
      onSold?.();
    } catch (e2) { toast.error(formatApiError(e2)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!unit} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="sell-dialog">
        <DialogHeader><DialogTitle>Sell unit — {unit.serial_number}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Which client is receiving this unit and at which of their sites?</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Client *</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="mt-1" data-testid="sell-client-select"><SelectValue placeholder="Choose client" /></SelectTrigger>
              <SelectContent>
                {clients.length === 0 && <SelectItem value="none" disabled>No clients yet. Create one first.</SelectItem>}
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Site address *</Label>
            <Select value={addressId} onValueChange={setAddressId} disabled={!clientDetail}>
              <SelectTrigger className="mt-1" data-testid="sell-address-select"><SelectValue placeholder={clientDetail ? "Choose site" : "Pick client first"} /></SelectTrigger>
              <SelectContent>
                {clientDetail?.billing_address && <SelectItem value="__billing__">Billing — {clientDetail.billing_address.slice(0, 50)}</SelectItem>}
                {(clientDetail?.shipping_addresses || []).map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.label} — {a.line.slice(0, 40)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nickname (optional)</Label>
              <Input value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder='e.g. "Blue press"' className="mt-1" data-testid="sell-nickname-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sold date</Label>
              <Input type="date" value={soldDate} onChange={(e) => setSoldDate(e.target.value)} className="mt-1" data-testid="sell-date-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving || !clientId} className="bg-indigo-600 hover:bg-indigo-700" data-testid="sell-save-btn">
              {saving ? "Saving..." : "Confirm sale"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Edit unit (nickname always; serial + location admin-only) ----------
function NicknameDialog({ unit, onClose, onSaved }) {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [nickname, setNickname] = useState("");
  const [serial, setSerial] = useState("");
  const [location, setLocation] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setNickname(unit?.nickname || "");
    setSerial(unit?.serial_number || "");
    setLocation(unit?.location || "");
  }, [unit]);
  if (!unit) return null;

  const serialChanged = isAdmin && serial.trim() && serial.trim() !== (unit.serial_number || "");
  const locationChanged = isAdmin && location.trim() !== (unit.location || "");

  const save = async (e) => {
    e.preventDefault();
    if (serialChanged) {
      const ok = window.confirm(
        `You are about to change this unit's serial number:\n\n${unit.serial_number}  →  ${serial.trim()}\n\nSerial numbers are the anchor for quotations, demo bookings, and AMC records. This change will be logged in the Audit Log. Continue?`
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const payload = { ...unit, nickname };
      if (isAdmin) {
        payload.serial_number = serial.trim();
        payload.location = location.trim();
      }
      await api.put(`/inventory/${unit.id}`, payload);
      toast.success(serialChanged ? "Serial number updated (logged in Audit Log)" : "Unit updated");
      onSaved?.();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!unit} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="nickname-dialog">
        <DialogHeader><DialogTitle>Edit machine unit</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Serial number</Label>
              {isAdmin ? (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-600">Admin — audited</span>
              ) : (
                <span className="text-[10px] text-slate-400">Read-only</span>
              )}
            </div>
            {isAdmin ? (
              <>
                <Input
                  value={serial}
                  onChange={(e) => setSerial(e.target.value)}
                  data-testid="edit-serial-input"
                  className="mt-1 font-mono"
                  required
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Changing this rewrites the anchor used by quotations, demo bookings, and AMC records. Old and new values are stored in the Audit Log.
                </p>
              </>
            ) : (
              <>
                <div className="mt-1 font-mono text-sm text-slate-700">{unit.serial_number}</div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Serial number cannot be changed after creation to preserve linked records. Ask an Admin if this was entered incorrectly.
                </p>
              </>
            )}
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nickname</Label>
            <Input value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder='e.g. "Blue press"' data-testid="nickname-input" className="mt-1" />
          </div>
          {isAdmin && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Location</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="edit-location-input" className="mt-1" />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="nickname-save-btn">
              {saving ? "Saving…" : (serialChanged ? "Save & log change" : "Save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Machine History ----------
function MachineHistoryDialog({ unit, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!unit) { setDetail(null); return; }
    (async () => {
      try { const r = await api.get(`/inventory/${unit.id}/timeline`); setDetail(r.data); } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [unit]);
  const events = useMemo(() => {
    const arr = [...((detail?.timeline) || [])];
    return arr.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  }, [detail]);
  if (!unit) return null;
  const daysInInventory = detail?.received_date ? Math.max(0, Math.floor((Date.now() - new Date(detail.received_date).getTime()) / 86400000)) : null;
  const iconFor = (t) => {
    switch (t) {
      case "received": return <PackagePlus className="w-2 h-2 text-white" />;
      case "demo_reserved": return <MapPinned className="w-2 h-2 text-white" />;
      case "demo_returned": return <RotateCcw className="w-2 h-2 text-white" />;
      case "demo_cancelled": return <XCircle className="w-2 h-2 text-white" />;
      case "demo_converted_to_sale": return <ShoppingCart className="w-2 h-2 text-white" />;
      case "status_change": return <ArrowRight className="w-2 h-2 text-white" />;
      case "location_change": return <Home className="w-2 h-2 text-white" />;
      case "nickname_change": return <Tag className="w-2 h-2 text-white" />;
      case "sold": return <ShoppingCart className="w-2 h-2 text-white" />;
      default: return <Clock className="w-2 h-2 text-white" />;
    }
  };
  const bgFor = (t) => ({
    received: "bg-emerald-500",
    demo_reserved: "bg-amber-500",
    demo_returned: "bg-blue-500",
    demo_cancelled: "bg-slate-400",
    demo_converted_to_sale: "bg-violet-500",
    status_change: "bg-indigo-500",
    location_change: "bg-slate-500",
    nickname_change: "bg-slate-500",
    sold: "bg-violet-600",
  }[t] || "bg-slate-400");
  const labelFor = (e) => {
    switch (e.type) {
      case "received": return <span>Received at <b>{e.location}</b> · status <b>{e.status}</b></span>;
      case "demo_reserved": return <span>Reserved for demo with <b>{e.client_name || "client"}</b> on {formatDMYTime(e.scheduled_at)}</span>;
      case "demo_returned": return <span>Demo returned</span>;
      case "demo_cancelled": return <span>Demo cancelled</span>;
      case "demo_converted_to_sale": return <span>Demo converted to sale</span>;
      case "status_change": return <span>Status <b>{e.from}</b> → <b>{e.to}</b>{e.to === "Rental" && (e.rental_client || e.rental_site) ? <> · {[e.rental_client, e.rental_site].filter(Boolean).join(" · ")}</> : null}</span>;
      case "rental_details_change": return <span>Rental details updated · {[e.rental_client, e.rental_site].filter(Boolean).join(" · ") || "cleared"}</span>;
      case "location_change": return <span>Location <b>{e.from}</b> → <b>{e.to}</b></span>;
      case "nickname_change": return <span>Nickname <b>{e.from || "(none)"}</b> → <b>{e.to || "(none)"}</b></span>;
      case "sold": return <span>Sold to <b>{e.client_name}</b> at <b>{e.address_label}</b> on {formatDMY(e.sold_date)}{e.from === "Rental" ? " (from Rental)" : ""}</span>;
      default: return <span>{e.type}</span>;
    }
  };
  return (
    <Dialog open={!!unit} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="machine-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Machine history — {unit.serial_number}</span>
            <Badge className={`border ${STATUS_STYLES[detail?.status || unit.status]}`} variant="outline">{detail?.status || unit.status}</Badge>
          </DialogTitle>
        </DialogHeader>
        {detail && (
          <div className="text-xs text-slate-600 space-y-1 border border-slate-200 rounded-md bg-slate-50 p-3">
            <div><b>Model:</b> {unit.catalog?.model_name || "—"} ({unit.catalog?.manufacturer || "—"})</div>
            {detail.nickname && <div><b>Nickname:</b> {detail.nickname}</div>}
            <div><b>Received:</b> {formatDMY(detail.received_date)} {daysInInventory != null && <span className="text-slate-500">· {daysInInventory} days in inventory</span>}</div>
            {detail.sold_to_address_label && <div><b>Installed at:</b> {detail.sold_to_address_label}</div>}
          </div>
        )}
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Activity</div>
        {events.length === 0 ? (
          <div className="text-center text-slate-400 text-sm py-6">No history yet.</div>
        ) : (
          <ol className="relative border-l-2 border-slate-100 ml-3 space-y-4" data-testid="machine-timeline-list">
            {events.map((e, i) => (
              <li key={i} className="ml-4" data-testid={`machine-event-${e.type}`}>
                <span className={`absolute -left-[9px] w-4 h-4 rounded-full flex items-center justify-center border-2 border-white ${bgFor(e.type)}`}>{iconFor(e.type)}</span>
                <div className="text-sm text-slate-900">{labelFor(e)}</div>
                <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                  <Clock className="w-3 h-3" />
                  {formatDMYTime(e.at)}
                  {e.actor && <span>· {e.actor}</span>}
                </div>
              </li>
            ))}
          </ol>
        )}
        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Bulk import ----------
function BulkImportDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { if (!open) { setFile(null); setResult(null); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a CSV file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/inventory/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(res.data);
      toast.success(`Imported ${res.data.created_count} · Skipped ${res.data.skipped_count}`);
      if (res.data.created_count > 0) onDone?.();
    } catch (e2) { toast.error(formatApiError(e2)); } finally { setUploading(false); }
  };

  const downloadTemplate = () => {
    const csv = "model_name,serial_number,location,status,received_date,nickname\nCNC-500,SN-EXAMPLE-1,Mumbai HQ,In Stock,2026-02-19,Blue press\nCNC-500,SN-EXAMPLE-DEMO,Mumbai HQ,Demo Pool,2026-02-19,Demo unit\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "inventory-template.csv"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="bulk-import-dialog">
        <DialogHeader><DialogTitle>Bulk import stock (CSV)</DialogTitle></DialogHeader>
        <div className="text-sm text-slate-600 space-y-2">
          <p>Upload a CSV with columns: <code className="text-xs bg-slate-100 rounded px-1">model_name</code> (or <code className="text-xs bg-slate-100 rounded px-1">catalog_id</code>), <code className="text-xs bg-slate-100 rounded px-1">serial_number</code>, <code className="text-xs bg-slate-100 rounded px-1">location</code>, <code className="text-xs bg-slate-100 rounded px-1">status</code>, <code className="text-xs bg-slate-100 rounded px-1">received_date</code>, <code className="text-xs bg-slate-100 rounded px-1">nickname</code>.</p>
          <p className="text-xs text-slate-500">Duplicate serials and unknown models will be skipped with a reason. Only <b>In Stock</b> or <b>Demo Pool</b> statuses are allowed via bulk import.</p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div className="flex items-center gap-2">
            <Input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="bulk-file-input" />
            <Button type="button" variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn">Template</Button>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            <Button type="submit" disabled={uploading || !file} className="bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-upload-btn">
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </form>

        {result && (
          <div className="border-t border-slate-200 pt-3 text-sm" data-testid="bulk-result">
            <div className="flex gap-3 mb-2">
              <span className="text-emerald-700 font-semibold" data-testid="bulk-created-count">Created: {result.created_count}</span>
              <span className="text-amber-700 font-semibold" data-testid="bulk-skipped-count">Skipped: {result.skipped_count}</span>
            </div>
            {result.skipped?.length > 0 && (
              <div className="max-h-40 overflow-y-auto border border-slate-100 rounded-md p-2 text-xs bg-slate-50">
                {result.skipped.map((s, i) => (
                  <div key={i} className="text-slate-700">Row {s.row}: <span className="font-mono">{s.serial || "—"}</span> — <span className="text-amber-700">{s.reason}</span></div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Reserve dialog ----------
function ReserveDialog({ unit, onClose, onReserved }) {
  const { user: me } = useAuth();
  const canQuote = can(me, "quotations", "view");
  const [leads, setLeads] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [leadId, setLeadId] = useState("");
  const [quotationId, setQuotationId] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!unit) return;
    (async () => {
      try {
        const [l, qres] = await Promise.all([api.get("/leads"), api.get("/quotations").catch(() => ({ data: [] }))]);
        setLeads(l.data || []);
        setQuotations(qres.data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
    setLeadId(""); setQuotationId(""); setNote(""); setQ("");
  }, [unit?.id]);

  if (!unit) return null;

  const filteredLeads = leads.filter((l) => !q || (l.company_name || "").toLowerCase().includes(q.toLowerCase()));
  const filteredQuotations = quotations.filter((qt) =>
    !q ||
    (qt.quotation_number || "").toLowerCase().includes(q.toLowerCase()) ||
    (qt.lead?.company_name || "").toLowerCase().includes(q.toLowerCase())
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!leadId && !quotationId) { toast.error("Pick a Lead or Quotation to reserve against"); return; }
    setSaving(true);
    try {
      await api.post(`/inventory/${unit.id}/reserve`, {
        reserved_for_lead_id: leadId,
        reserved_for_quotation_id: quotationId,
        note,
      });
      toast.success("Unit reserved");
      onReserved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!unit} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="reserve-dialog">
        <DialogHeader><DialogTitle>Reserve this unit</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-md bg-orange-50 border border-orange-200 p-3 text-sm">
            <div className="text-slate-600 text-xs uppercase tracking-wider">Reserving</div>
            <div className="font-mono text-slate-900">{unit.serial_number}</div>
            <div className="text-xs text-slate-500">{unit.catalog?.model_name || unit.nickname || ""}</div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Search</Label>
            <Input placeholder={canQuote ? "Type a company name or quotation number…" : "Type a company name…"} value={q} onChange={(e) => setQ(e.target.value)} className="mt-1" data-testid="reserve-search" />
          </div>
          {canQuote && <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reserve against Quotation</Label>
            <Select value={quotationId || "__none__"} onValueChange={(v) => setQuotationId(v === "__none__" ? "" : v)}>
              <SelectTrigger className="mt-1" data-testid="reserve-quotation-select"><SelectValue placeholder="— None —" /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="__none__">— None —</SelectItem>
                {filteredQuotations.slice(0, 100).map((qt) => (
                  <SelectItem key={qt.id} value={qt.id}>
                    <span className="font-mono">{qt.quotation_number}</span>
                    {qt.lead?.company_name ? <span className="text-slate-500"> — {qt.lead.company_name}</span> : null}
                    {qt.status ? <span className="text-slate-400"> · {qt.status}</span> : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{canQuote ? "Or, reserve against Lead" : "Reserve against Lead"}</Label>
            <Select value={leadId || "__none__"} onValueChange={(v) => setLeadId(v === "__none__" ? "" : v)}>
              <SelectTrigger className="mt-1" data-testid="reserve-lead-select"><SelectValue placeholder="— None —" /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="__none__">— None —</SelectItem>
                {filteredLeads.slice(0, 100).map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    <span className="font-medium">{l.company_name}</span>
                    {l.contact_person ? <span className="text-slate-500"> — {l.contact_person}</span> : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1">Pick at least one — Quotation, Lead, or both.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" placeholder="Optional — e.g. 'Client visit 15 Sep'" data-testid="reserve-note" />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="reserve-save-btn">
              {saving ? "Reserving…" : "Reserve unit"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Release confirmation ----------
function ReleaseDialog({ unit, onClose, onReleased }) {
  const [saving, setSaving] = useState(false);
  if (!unit) return null;
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/inventory/${unit.id}/release`);
      toast.success("Reservation released — unit back In Stock");
      onReleased?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!unit} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="release-dialog">
        <DialogHeader><DialogTitle>Release reservation?</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            This will return unit <span className="font-mono">{unit.serial_number}</span> to <b>In Stock</b>.
            The reservation history will be preserved on the unit's timeline.
          </p>
          <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs">
            {unit.reserved_for_quotation && <div>Quotation: <span className="font-mono">{unit.reserved_for_quotation.quotation_number}</span></div>}
            {unit.reserved_for_lead && <div>Lead: <b>{unit.reserved_for_lead.company_name}</b></div>}
            {unit.reserved_by_name && <div>Reserved by: {unit.reserved_by_name}</div>}
            {unit.reserved_note && <div>Note: {unit.reserved_note}</div>}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Keep reserved</Button>
            <Button onClick={submit} disabled={saving} className="bg-slate-800 hover:bg-slate-900" data-testid="release-confirm-btn">
              {saving ? "Releasing…" : "Release"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
